import React, { useMemo, useRef, useState } from 'react';
import { Button } from 'rebass';
import DatePicker from 'react-datepicker';
import moment from 'moment-timezone';

import { useCustomPersonDataKeys } from 'context/CustomPersonDataKeysContext';
import { useAuth } from 'context/AuthContext';
import { formatDate } from 'utils/formatDate';
import { DATE_FORMATS } from 'utils/dateFormats';
import { FiChevronDown } from 'react-icons/fi';
import { useAuditTrailInfiniteQuery } from 'api/useAuditTrailInfiniteQuery';
import LoadingIndicator from 'components/LoadingIndicator';
import { Label } from '@rebass/forms';
import { TableV2 } from 'components/Table';
import NiceDropdown from 'components/NiceDropdown';
import Portal from 'components/Portal';
import { PositionerV2 } from 'components/Positioner';
import { ProtectedField, useProtectedCompactRender } from 'components/ProtectedField';
import { expandableCell } from 'components/ExpandableText';
import servicePeople from 'services/people';

/**
 * Reusable audit trail table component.
 * Fetches and displays field-level change history for a person with infinite loading.
 *
 * @param {Object} props
 * @param {number|string} props.personId - The person ID to fetch audit trail for
 * @param {number|string} [props.initialFieldId] - Optional person data key ID to pre-select in the field filter
 */
export default function AuditTrailTable({ personId, initialFieldId }) {
    const auth = useAuth();
    const customPersonDataKeys = useCustomPersonDataKeys();
    const [dateRangeDraft, setDateRangeDraft] = useState([null, null]);
    const [dateRange, setDateRange] = useState(dateRangeDraft);
    const [dateFromDraft, dateToDraft] = dateRangeDraft;
    const [dateFrom, dateTo] = dateRange;
    const [selectedFieldId, setSelectedFieldId] = useState(initialFieldId ? String(initialFieldId) : '');

    const compactRender = useProtectedCompactRender();

    const dataKeysMap = useMemo(() => {
        if (!customPersonDataKeys) return new Map();
        return new Map(customPersonDataKeys.map(key => [key.id, key]));
    }, [customPersonDataKeys]);

    const [isFieldDropdownOpen, setIsFieldDropdownOpen] = useState(false);
    const fieldDropdownAnchorRef = useRef();

    const groupedFieldItems = useMemo(() => {
        if (!customPersonDataKeys) return [];

        const groups = new Map();
        for (const key of customPersonDataKeys) {
            const groupName = key.group?.name || 'Other';
            const groupOrder = key.group ? key.group.order : Infinity;
            if (!groups.has(groupName)) {
                groups.set(groupName, { name: groupName, order: groupOrder, keys: [] });
            }
            groups.get(groupName).keys.push(key);
        }

        const sortedGroups = [...groups.values()].sort((a, b) => a.order - b.order);

        const items = [];
        for (const group of sortedGroups) {
            items.push({ id: `group:${group.name}`, title: group.name, label: true });
            const sortedKeys = [...group.keys].sort((a, b) => a.order - b.order);
            for (const key of sortedKeys) {
                items.push({ id: String(key.id), title: key.title });
            }
        }

        return items;
    }, [customPersonDataKeys]);

    const selectedFieldLabel = useMemo(() => {
        if (!selectedFieldId) return 'All panel properties';
        const item = groupedFieldItems.find(i => i.id === selectedFieldId);
        return item?.title || 'All panel properties';
    }, [selectedFieldId, groupedFieldItems]);

    const formattedDateFrom = dateFrom ? moment(dateFrom).format(DATE_FORMATS.API_DATE) : undefined;
    const formattedDateTo = dateTo ? moment(dateTo).format(DATE_FORMATS.API_DATE) : undefined;
    const personDataKeyId = selectedFieldId ? Number(selectedFieldId) : undefined;

    const {
        data,
        isLoading,
        isError,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage
    } = useAuditTrailInfiniteQuery(personId, { dateFrom: formattedDateFrom, dateTo: formattedDateTo, personDataKeyId });

    const entries = useMemo(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap(page => page.data);
    }, [data]);

    const resolveFieldName = personDataKeyId => {
        const key = dataKeysMap.get(personDataKeyId);
        return key ? key.title : 'Unknown field';
    };

    const formatTimestamp = timestamp => {
        if (!timestamp) return '';
        return formatDate(timestamp, { timezone: auth.user.timezone, includeTime: true });
    };

    /**
     * Builds a ProtectedField adapter for an audit trail record value.
     *
     * @param {Object} record - The audit trail record from the API.
     * @param {'old'|'new'} field - Which value to display (old or new).
     * @returns {import('components/ProtectedField').ProtectedFieldAdapter}
     */
    const buildAuditTrailAdapter = (record, field) => ({
        value: record[field],
        isPii: !!record.is_pii,
        mutationKey: ['pii-audit', record.id, field],
        mutationFn: () => servicePeople.getAuditTrailPiiValues(personId, record.id).then(data => data[field])
    });

    const renderAuditValue = (record, field) => {
        const personDataKey = dataKeysMap.get(record.person_data_key_id);

        return (
            <ProtectedField
                adapter={buildAuditTrailAdapter(record, field)}
                // A row puts the old and the new value side by side, so a single edit to a
                // long value can push every other entry off the screen (PFOX-1406).
                render={value => expandableCell(compactRender(value, personDataKey))}
                hideable
                empty="-"
            />
        );
    };

    const filterBar = (
        <div className="py-3 px-4 flex gap-4 items-end">
            <div>
                <Label className="!fs-accent-12" htmlFor="audit-trail-date-range">
                    Date range
                </Label>
                <DatePicker
                    id="audit-trail-date-range"
                    className="theme-input datepicker-input !w-[240px]"
                    selectsRange
                    startDate={dateFromDraft}
                    endDate={dateToDraft}
                    onChange={update => setDateRangeDraft(update)}
                    onClickOutside={() => setDateRange(dateRangeDraft)}
                    maxDate={new Date()}
                    isClearable
                    shouldCloseOnSelect={false}
                    placeholderText="Select date range"
                    dateFormat="MM/dd/yyyy"
                />
            </div>
            <div>
                <Label className="!fs-accent-12" htmlFor="audit-trail-field-filter">
                    Panel Property
                </Label>
                <Button
                    id="audit-trail-field-filter"
                    variant="secondary-gray"
                    className="!w-[240px] ellipsis !text-left !flex !justify-between !items-center"
                    ref={fieldDropdownAnchorRef}
                    onClick={() => setIsFieldDropdownOpen(true)}
                    aria-haspopup="listbox"
                    aria-expanded={isFieldDropdownOpen}
                >
                    <span>{selectedFieldLabel}</span>
                    <FiChevronDown role="img" aria-label="Arrow down" style={{ margin: 0 }} />
                </Button>
                <Portal isOpen={isFieldDropdownOpen}>
                    <PositionerV2 anchorRef={fieldDropdownAnchorRef}>
                        <NiceDropdown
                            style={{ inset: 'auto', position: 'static' }}
                            onClose={() => setIsFieldDropdownOpen(false)}
                            showSearch
                            searchAutoFocus={false}
                            width="240px"
                            onChange={value => {
                                setSelectedFieldId(value === 'all' ? '' : value);
                                setIsFieldDropdownOpen(false);
                            }}
                            items={[{ id: 'all', title: 'All panel properties' }, ...groupedFieldItems]}
                        />
                    </PositionerV2>
                </Portal>
            </div>
        </div>
    );

    if (isLoading) {
        return (
            <>
                {filterBar}
                <div className="px-8 py-4">
                    <LoadingIndicator />
                </div>
            </>
        );
    }

    if (isError) {
        return (
            <>
                {filterBar}
                <div className="px-8 py-4">
                    <p className="color-danger">Failed to load audit trail.</p>
                </div>
            </>
        );
    }

    if (entries.length === 0) {
        return (
            <>
                {filterBar}
                <div className="px-8 py-4">No audit trail entries found.</div>
            </>
        );
    }

    return (
        <div className="grow overflow-auto">
            {filterBar}
            <TableV2
                data={entries}
                rowHoverable
                fixed
                headerProps={{ className: 'sticky top-0 z-1 bg-white' }}
                columns={[
                    {
                        key: 'field',
                        title: 'Field',
                        width: '20%',
                        render: record => resolveFieldName(record.person_data_key_id),
                        onCell: () => ({
                            className: 'color-text-primary font-medium fs-body-14'
                        })
                    },
                    {
                        key: 'old-value',
                        title: 'Old Value',
                        width: '25%',
                        render: record => renderAuditValue(record, 'old'),
                        onCell: () => ({
                            className: 'color-text-secondary fs-body-14'
                        })
                    },
                    {
                        key: 'new-value',
                        title: 'New Value',
                        width: '25%',
                        render: record => renderAuditValue(record, 'new'),
                        onCell: () => ({
                            className: 'color-text-secondary fs-body-14'
                        })
                    },
                    {
                        key: 'changed',
                        title: 'Changed',
                        width: '30%',
                        render: record => (
                            <div>
                                <div className="color-text-secondary fs-body-14">{record.reference || '-'}</div>
                                <div className="color-text-muted fs-body-12">{formatTimestamp(record.updated_at)}</div>
                            </div>
                        ),
                        onCell: () => ({
                            className: 'color-text-secondary fs-body-14'
                        })
                    }
                ]}
            />

            {hasNextPage && (
                <Button
                    variant="secondary-gray"
                    className="!m-4"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                >
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                </Button>
            )}
        </div>
    );
}
